/**
 * Encrypts the given data using PBKDF2 and AES-GCM encryption.
 *
 * This function derives an encryption key from a password and salt using PBKDF2,
 * then uses that key to encrypt the provided data with AES-GCM. The encrypted
 * data and initialization vector (IV) are returned as base64-encoded strings.
 *
 * @param {Object} data - The data object to be encrypted.
 * @returns {Promise<{ encryptedBase64: string, ivBase64: string }>} - A promise that resolves to an object containing the base64-encoded encrypted data and IV.
 */
const PBKDF2_PASSWORD = process.env.REACT_APP_PBKDF2_PASSWORD;
const CREDENTIAL_SALT = process.env.REACT_APP_CREDENTIAL_SALT;

export const encryptData = async (data) => {
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
        'raw',
        encoder.encode(PBKDF2_PASSWORD), // This would typically be user-specific
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
    );
    
    // Generate AES encryption key
    const encryptionKey = await window.crypto.subtle.deriveKey(
        {
        name: 'PBKDF2',
        salt: encoder.encode(CREDENTIAL_SALT),
        iterations: 100000,
        hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
    );
    
    // Generate IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt the credentials
    const encryptedData = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        encryptionKey,
        encoder.encode(JSON.stringify(data))
    );
    
    // Convert to base64 for transmission
    const encryptedBase64 = btoa(
        String.fromCharCode(...new Uint8Array(encryptedData))
    );
    const ivBase64 = btoa(String.fromCharCode(...iv));
    
    return { encryptedBase64, ivBase64 };

}



export const decryptData = async (encryptedBase64, ivBase64) => {
    try {
        const password = PBKDF2_PASSWORD;
        const salt = new TextEncoder().encode(CREDENTIAL_SALT); // Convert salt to bytes
        const iterations = 100000;
        const keyLen = 256; // AES-256 (256-bit)
        
        // Convert Base64 inputs to Uint8Array
        const iv = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0)); // IV for AES-GCM
        const encryptedData = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));

        // Derive the key using PBKDF2
        const keyMaterial = await window.crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(password),
            { name: 'PBKDF2' },
            false,
            ['deriveKey']
        );

        const key = await window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt,
                iterations,
                hash: 'SHA-256',
            },
            keyMaterial,
            { name: 'AES-GCM', length: keyLen },
            true,
            ['decrypt']
        );

        // Decrypt data using AES-GCM
        const decryptedBuffer = await window.crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            encryptedData
        );

        // Convert decrypted buffer to string
        const decryptedText = new TextDecoder().decode(decryptedBuffer);
        return JSON.parse(decryptedText);
    } catch (error) {
        console.error('Decryption error:', error);
        return null;
    }
};

